/**
 * THE DID AUTHORITY LADDER, ON THE SURFACE — how an attribution is read out to an operator.
 *
 * Requirement: `.kiro/specs/DID-AUTHORITY-LADDER.md`. The backend decides
 * (`backend/services/did_ladder.py`) and stores its decision on the row; this file turns those
 * two stored columns into what a reader sees, and NOTHING here re-decides anything.
 *
 * WHY IT MIRRORS THE BACKEND RATHER THAN CALLING IT. The same discipline
 * `components/transmissionTiming.ts` already follows for timestamp provenance: the rung ORDER and
 * the EXPOSURE set below are the backend's own, and `tests/test_did_attribution_persisted.py` reads
 * both files and fails when they drift. Two surfaces disagreeing about what rung 3 is worth
 * destroys the only thing the ladder exists to provide — that the source of an attribution is
 * stated, and stated the same way wherever it is read.
 *
 * IT STATES THE SOURCE; IT DOES NOT WARN. PR #174 gave rungs 3-5 an amber `⚠️` badge and the
 * operator declined that framing — *"I dont think we need to display a WARNING, but we should be
 * clear about where the authoritative timestamp came from."* So every recorded attribution carries
 * its source, a `conflict` remains reported as a FAULT (declining warnings is not declining error
 * reporting), and a row with nothing recorded says so.
 *
 * No imports and no JSX, deliberately, so a test can transpile this file and EXECUTE it against a
 * real row. "The marking reflects the stored columns for THAT ROW" is a claim about output, and
 * only running it establishes that.
 */

/** MUST equal backend `did_ladder.DID_RUNGS`, strongest first. Pinned by a drift test. */
export const DID_RUNGS: string[] = [
  'did_run_token',
  'did_mailbox_local',
  'did_provider_field',
  'did_body_label',
  'did_attachment_name',
  'did_ocr_page',
]

/**
 * MUST equal backend `did_ladder.DID_UNCERTAIN_RUNGS`.
 *
 * NO LONGER A WARNING SET — IT IS AN EXPOSURE SET. The operator's ruling, verbatim: *"I dont
 * think we need to display a WARNING, but we should be clear about where the authoritative
 * timestamp came from."* So a value from one of these rungs is stated as plainly as any other,
 * and what this set adds is one further CLAUSE OF FACT about the source — rung 3 is a label in
 * vendor-owned prose, rungs 4 and 5 are incidental and image-quality dependent — rather than a
 * caution badge on the subset we judged shaky. The top two are assertions: our own mailbox
 * naming, and a field the provider states machine-readably.
 *
 * It stays mirrored from the backend because that is what stops the two surfaces disagreeing
 * about what rung 3 IS; the mirror is pinned by `tests/test_did_attribution_persisted.py`.
 */
export const DID_UNCERTAIN_RUNGS: string[] = [
  'did_body_label',
  'did_attachment_name',
  'did_ocr_page',
]

export const LADDER_OUTCOME_RESOLVED = 'resolved'
export const LADDER_OUTCOME_CONFLICT = 'conflict'
export const LADDER_OUTCOME_NONE = 'none'

/**
 * What each rung is, in the words an operator reads. Keyed by the STORED code.
 *
 * The wording states the SOURCE and its exposure in the same breath, because the rung code alone
 * (`did_body_label`) tells a reader nothing about why it ranks where it does.
 */
export const DID_RUNG_LABELS: Record<string, string> = {
  did_run_token:
    'a high-confidence match on the run token we minted — our own identifier, then our own configuration',
  did_mailbox_local: 'the mailbox it was delivered to — our own configuration',
  did_provider_field: "a field the provider stated machine-readably",
  did_body_label: 'a labelled line in the notification body — wording the vendor can change',
  did_attachment_name: 'the attachment filename — incidental, not a contract',
  did_ocr_page: 'OCR of page 1 — depends on image quality',
}

/**
 * The SAME source in two or three words, for a LIST cell. Keyed by the stored code.
 *
 * A listing row has no room for the sentence above and an operator scanning one still has to be
 * able to tell a mailbox attribution from an OCR'd one, so the compact form names the source and
 * nothing else — no adjective, no severity, no icon. `DID_RUNG_LABELS` remains the long form for
 * a detail surface and a tooltip; these two must describe the same rung, which
 * `tests/test_did_attribution_persisted.py` pins by requiring an entry here for every rung.
 */
export const DID_RUNG_SHORT_LABELS: Record<string, string> = {
  did_run_token: 'run token',
  did_mailbox_local: 'mailbox',
  did_provider_field: 'provider field',
  did_body_label: 'body label',
  did_attachment_name: 'filename',
  did_ocr_page: 'OCR',
}

/** The three fields a list row or a detail row carries. */
export interface DidAttributionSource {
  did_attributed?: string | null
  did_attribution_rung?: string | null
  did_attribution_outcome?: string | null
  did_attribution_detail?: Record<string, unknown> | null
}

export interface DidAttributionDescription {
  /** True when the row records no attribution at all — every historical row. */
  notRecorded: boolean
  /** The DID as written, or null when the ladder refused or nothing was recorded. */
  did: string | null
  /** The stored rung code, or null. */
  rung: string | null
  /** The spec's 1..5 position for `rung`, DERIVED from the ladder order, or null. */
  rungNumber: number | null
  /** `resolved` | `conflict` | `none` | null. */
  outcome: string | null
  /**
   * THE SOURCE, IN TWO OR THREE WORDS — always present for a row that recorded an attribution.
   *
   * This replaced the amber `⚠️` badge PR #174 showed on the subset we judged shaky. The operator
   * declined that framing: *"I dont think we need to display a WARNING, but we should be clear
   * about where the authoritative timestamp came from."* So every recorded attribution states its
   * source and no recorded attribution is singled out as a caution.
   *
   * Empty ONLY when nothing was recorded — which is what makes an empty cell in the listing mean
   * exactly one thing. Under the old rule an unmarked row was either clean or unrecorded and a
   * reader could not tell which.
   */
  sourceShort: string
  /**
   * A FAULT, not a caution: comparable rungs supplied DIFFERENT DIDs.
   *
   * Declining warnings is not declining error reporting. A conflict means a misconfigured job or
   * a misrouted fax — something is wrong in the world, not merely weakly sourced — so it stays
   * visible as a problem (spec rule 4). `resolved` from any rung, `none`, and not-recorded are
   * all `false`: each of those is a statement about where a value came from, or about its absence.
   */
  fault: boolean
  /**
   * The rung is in `DID_UNCERTAIN_RUNGS` — i.e. its source depends on something outside our
   * control. Carried as a FACT ABOUT THE SOURCE that `text` states in words, never as a severity:
   * nothing in the UI colours or badges a row on this flag.
   */
  exposed: boolean
  /** One sentence for a tooltip or a detail row. Empty when nothing was recorded. */
  text: string
}

/**
 * How to read one row's stored attribution.
 *
 * ALWAYS SAY WHERE IT CAME FROM, NEVER WARN. `sourceShort` is populated for every recorded
 * attribution rather than for the subset a warning rule selected, because a badge that appears
 * only when we distrust a value teaches a reader to read its ABSENCE as trust — and the absence
 * covered both "rung 1" and "nothing recorded". The one thing still reported as a problem is a
 * `conflict`, which is a fault in the world rather than a weak source.
 *
 * NOT RECORDED IS NOT AN ATTRIBUTION, and that distinction is why `notRecorded` is its own flag.
 * Every row measured before these columns existed holds NULL; naming any source for one of them
 * would assert a property of a measurement nobody made — the no-backfill rule reaching the UI.
 */
export function describeDidAttribution(
  src: DidAttributionSource,
): DidAttributionDescription {
  const outcome = src.did_attribution_outcome || null
  const rung = src.did_attribution_rung || null
  const did = src.did_attributed || null
  const index = rung ? DID_RUNGS.indexOf(rung) : -1
  const rungNumber = index >= 0 ? index + 1 : null

  if (!outcome) {
    return {
      notRecorded: true,
      did,
      rung,
      rungNumber,
      outcome: null,
      sourceShort: '',
      fault: false,
      exposed: false,
      text: '',
    }
  }

  if (outcome === LADDER_OUTCOME_CONFLICT) {
    return {
      notRecorded: false,
      did: null,
      rung: null,
      rungNumber: null,
      outcome,
      sourceShort: 'DID conflict',
      fault: true,
      exposed: false,
      text:
        'The sources DISAGREED about which DID this fax arrived for, so no attribution was ' +
        'made. That is a configuration or provider fault worth investigating, not a tie to ' +
        'break — the disagreeing values are recorded below.',
    }
  }

  if (outcome === LADDER_OUTCOME_NONE) {
    return {
      notRecorded: false,
      did: null,
      rung: null,
      rungNumber: null,
      outcome,
      sourceShort: 'no source',
      fault: false,
      exposed: false,
      text:
        'No source could say which DID this fax arrived for. Every rung was tried and each was ' +
        'silent or unbuilt; the reasons are recorded below.',
    }
  }

  const label = (rung && DID_RUNG_LABELS[rung]) || 'an unrecognised rung'
  const short = (rung && DID_RUNG_SHORT_LABELS[rung]) || 'unrecognised source'
  const exposed = rung === null || DID_UNCERTAIN_RUNGS.indexOf(rung) !== -1
  return {
    notRecorded: false,
    did,
    rung,
    rungNumber,
    outcome,
    sourceShort: short,
    fault: false,
    exposed,
    text:
      `Attributed to ${did ?? 'an unrecorded number'} from ${label}` +
      (rungNumber ? ` (rung ${rungNumber} of ${DID_RUNGS.length})` : '') +
      (exposed
        ? '. That source is outside our control and can change or go silent without notice, ' +
          'which is why it sits where it does on the ladder.'
        : '.'),
  }
}
